import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Clock3 } from "lucide-react";
import { toast } from "sonner";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Comprehensive F&B taxonomy — café was the original focus, but we now serve
// any food/beverage business. Grouped roughly by category so chips wrap into
// natural clusters (coffee/brew → bakery/dessert → cuisines → drinks →
// venue style → other). Order is intentional, not alphabetical — most
// common picks (specialty coffee, brunch, bakery) lead.
const CAFE_TYPES = [
    // Coffee & brew
    "specialty coffee",
    "kopitiam",
    "roastery",
    // Bakery / sweets
    "brunch",
    "bakery",
    "dessert",
    "ice cream / gelato",
    "patisserie / cake shop",
    // Tea & drinks
    "matcha bar",
    "boba / bubble tea",
    "tea house",
    "juice / smoothie bar",
    "wine / cocktail bar",
    "beverage / drinks",
    // Cuisine focus
    "Malay / Nasi Lemak",
    "Chinese cuisine",
    "Korean cuisine",
    "Japanese cuisine",
    "Indian cuisine",
    "Vietnamese cuisine",
    "Thai cuisine",
    "Middle Eastern",
    "Western cuisine",
    "Asian fusion",
    // Format
    "noodle / mamak",
    "snacks / kuih / street food",
    "light meals / sandwich",
    "salad / healthy",
    "halal café",
    "vegan / plant-based",
    "bistro",
    // Venue style
    "garden / outdoor",
    "co-working café",
    "pet-friendly",
    "rooftop / view",
    "other",
];
const CUSTOMER_PROFILES = [
    "students",
    "young white-collar",
    "families",
    "digital nomads",
    "tourists",
    "regulars",
    "couples / dating",
    "content creators",
    "foodies",
    "expats",
    "mums with kids",
    "Muslim families",
    "Gen Z",
    "millennials",
    "weekend crowd",
    "post-work crowd",
    "seniors",
];

const ONE_WORD_OPTIONS = [
    { id: "slow", label: "Slow", help: "慢 · unhurried, mindful" },
    { id: "warm", label: "Warm", help: "温暖 · cozy, inviting" },
    { id: "home", label: "Home", help: "家 · safe, familiar" },
    { id: "sharp", label: "Sharp", help: "精致 · refined, editorial" },
    { id: "craft", label: "Craft", help: "匠心 · made with care" },
    { id: "quiet", label: "Quiet", help: "静 · calm, reflective" },
    { id: "bold", label: "Bold", help: "大胆 · loud, confident" },
    { id: "playful", label: "Playful", help: "好玩 · fun, cheeky" },
];

const GOAL_OPTIONS = [
    { id: "sales", emoji: "📈", label: "Drive sales", help: "Push menu items, repeat customers, urgency CTAs" },
    { id: "brand", emoji: "🌟", label: "Build brand", help: "Identity, recognition, founder story, values" },
    { id: "new_menu", emoji: "🆕", label: "Launch new menu", help: "Teaser → reveal → prove arc for specific items" },
    { id: "reach", emoji: "👥", label: "Reach new customers", help: "Strong hooks, share-worthy, follower growth" },
    { id: "retention", emoji: "🔁", label: "Retain regulars", help: "Loyalty, community, member-only feel" },
    { id: "event", emoji: "🎉", label: "Big event", help: "Opening, anniversary, relocation countdown" },
];

export default function Intake() {
    const [params] = useSearchParams();
    const orderId = params.get("order");

    const [loading, setLoading] = useState(true);
    const [order, setOrder] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [form, setForm] = useState({
        cafe_name: "",
        ig_handle: "",
        location: "",
        cafe_type: [],
        customer_profile: [],
        one_word: "",
        top_menu: "",
        tone_refs: "",
        avoid: "",
        goals: [],
        hero_menu: [],
        start_date: "",
        include_holidays: true,
        venue_refs: [],          // [{slot, label, image_url}] — 5 REQUIRED store photos
        venue_description: "",   // 1-line description (optional fallback)
        product_refs: [],        // [{name, image_url}] photos per hero menu item — REQUIRED
    });

    const [uploadingVenue, setUploadingVenue] = useState(null);  // slot number in flight, or null
    const [uploadingProduct, setUploadingProduct] = useState(null); // hero_menu item name in flight, or null

    // Starter customers get a lighter intake — only 7 single photos are
    // delivered, so the 5-angle venue shoot (Q13) is overkill. Pro/Premium
    // produce 30 days of content INSIDE the customer's real shop, so for
    // those tiers the venue photos are still strictly required.
    const isStarter = order?.package_id === "kit-starter";

    /** Upload ONE venue photo for the given slot (1-5) tagged with its angle
     *  label. Each slot lives independently — uploading slot 2 doesn't touch
     *  slot 1 etc. Replaces a slot's existing photo if the customer picks a
     *  new file for that same angle. */
    const onVenueImageChange = async (e, slot, label) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error(`${file.name} too large — max 10MB`);
            if (e.target) e.target.value = "";
            return;
        }
        setUploadingVenue(slot);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await axios.post(
                `${API}/orders/${orderId}/venue_image?slot=${slot}`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            setForm(prev => ({
                ...prev,
                // Drop any previous entry for this slot, then push the new one.
                // Keeps venue_refs flat without duplicates per angle.
                venue_refs: [
                    ...(prev.venue_refs || []).filter(v => v.slot !== slot),
                    { slot, label, image_url: data.url },
                ],
            }));
            toast.success(`${label} photo uploaded ✓`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Venue upload failed");
        } finally {
            setUploadingVenue(null);
            if (e.target) e.target.value = "";
        }
    };

    const removeVenueRef = (slot) => {
        setForm(prev => ({
            ...prev,
            venue_refs: (prev.venue_refs || []).filter(v => v.slot !== slot),
        }));
    };

    /** Upload one or more product photos tagged to a specific hero menu item.
     *  Backend reuses the venue_image endpoint pattern but we splice into
     *  `product_refs: [{name, image_url}]` so the AI pipeline matches the
     *  right photo to the right theme day later. Required path — submission
     *  is blocked until every hero_menu item has at least one photo. */
    const onProductImageChange = async (e, itemName) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        // Cap at 3 photos per item — more is wasted prompt tokens + harder
        // for AI to weigh which angle matters most. Slice client-side so
        // multi-select doesn't sneak past the per-item-3 rule.
        const existingCount = (form.product_refs || []).filter(r => r.name === itemName).length;
        const room = 3 - existingCount;
        if (room <= 0) {
            toast.error(`Already 3 photos for ${itemName} — remove one to add another`);
            if (e.target) e.target.value = "";
            return;
        }
        const toUpload = files.slice(0, room);
        if (files.length > room) {
            toast.info(`Only uploading ${room} of ${files.length} — 3-photo max per item`);
        }
        setUploadingProduct(itemName);
        try {
            for (let i = 0; i < toUpload.length; i++) {
                const f = toUpload[i];
                if (f.size > 10 * 1024 * 1024) {
                    toast.error(`${f.name} too large — max 10MB`);
                    continue;
                }
                const fd = new FormData();
                fd.append("file", f);
                // slot is 1-3 within the per-item set, derived from current count
                const slot = existingCount + i + 1;
                const safeName = itemName.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30) || "product";
                const { data } = await axios.post(
                    `${API}/orders/${orderId}/product_image?slot=${slot}&product_name=${encodeURIComponent(safeName)}`,
                    fd,
                    { headers: { "Content-Type": "multipart/form-data" } },
                );
                setForm(prev => ({
                    ...prev,
                    product_refs: [...(prev.product_refs || []), { name: itemName, image_url: data.url }],
                }));
            }
            toast.success(`${toUpload.length} photo${toUpload.length > 1 ? "s" : ""} uploaded for ${itemName}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Product photo upload failed");
        } finally {
            setUploadingProduct(null);
            if (e.target) e.target.value = "";
        }
    };

    const removeProductRef = (itemName, idx) => {
        setForm(prev => {
            const matching = (prev.product_refs || []).filter(r => r.name === itemName);
            const targetUrl = matching[idx]?.image_url;
            return {
                ...prev,
                product_refs: (prev.product_refs || []).filter(r => !(r.name === itemName && r.image_url === targetUrl)),
            };
        });
    };

    useEffect(() => {
        if (!orderId) { setLoading(false); setNotFound(true); return; }
        (async () => {
            try {
                const { data } = await axios.get(`${API}/orders/${orderId}/public`);
                setOrder(data);
                if (data.intake_submitted) setSubmitted(true);
            } catch {
                setNotFound(true);
            } finally {
                setLoading(false);
            }
        })();
    }, [orderId]);

    const toggle = (key, value) => {
        setForm(prev => ({
            ...prev,
            [key]: prev[key].includes(value) ? prev[key].filter(v => v !== value) : [...prev[key], value]
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        const required = ["cafe_name", "ig_handle", "location", "one_word", "top_menu"];
        for (const f of required) {
            if (!form[f].trim()) {
                toast.error(`Please fill: ${f.replace("_", " ")}`);
                return;
            }
        }
        // Q9b — every hero menu item must have at least one product photo.
        // This is what stops AI from inventing wrong-shape dishes (the most
        // common reason customers reject delivered kits). The intake form is
        // the only place we can enforce this — once they submit, the 48h
        // clock starts and the AI runs whatever it has.
        // Skipped for Starter because that tier is text-only (no AI-render).
        if (!isStarter && form.hero_menu.length > 0) {
            const missingPhotos = form.hero_menu.filter(
                item => !(form.product_refs || []).some(r => r.name === item)
            );
            if (missingPhotos.length > 0) {
                toast.error(
                    `Please upload at least 1 product photo for: ${missingPhotos.join(", ")}`,
                    { description: "AI needs to see your actual dish — scroll up to Q9b.", duration: 8000 },
                );
                // Auto-scroll to the product photo uploader so customer can act fast
                document.querySelector('[data-testid="product-photo-uploader"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                return;
            }
        }
        // Q13 — all 5 venue angles required (Pro/Premium only). Without
        // these, AI invents a generic cafe environment that looks nothing
        // like the customer's real shop (#2 customer-rejection reason after
        // wrong dish shape). Starter only delivers 7 single photos so it
        // doesn't need the full venue shoot — we skip the check there.
        if (!isStarter) {
            const REQUIRED_SLOTS = [1, 2, 3, 4, 5];
            const filledSlots = new Set((form.venue_refs || []).map(v => v.slot));
            const missingSlots = REQUIRED_SLOTS.filter(s => !filledSlots.has(s));
            if (missingSlots.length > 0) {
                const slotNames = {
                    1: "Storefront", 2: "Dining area", 3: "Bar/counter", 4: "Kitchen", 5: "Detail",
                };
                toast.error(
                    `Please upload all 5 shop photos. Missing: ${missingSlots.map(s => slotNames[s]).join(", ")}`,
                    { description: "Scroll down to Q13 — phone snapshots are fine, they don't need to be pretty.", duration: 9000 },
                );
                document.querySelector('[data-testid="venue-uploader"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                return;
            }
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/orders/${orderId}/intake`, form);
            setSubmitted(true);
            toast.success("Brief received. Your kit lands in 48h ☕️");
        } catch (err) {
            if (err?.response?.status === 409) {
                setSubmitted(true);
                toast.info("You've already submitted this brief.");
            } else {
                toast.error(err?.response?.data?.detail || "Submit failed. Try again?");
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-cafe-cream flex items-center justify-center">
                <div className="text-cafe-mocha">Loading…</div>
            </div>
        );
    }

    if (notFound) {
        return (
            <div className="min-h-screen bg-cafe-cream text-cafe-espresso">
                <SiteNav />
                <main className="pt-40 pb-24 max-w-2xl mx-auto px-6 text-center">
                    <h1 className="font-serif text-5xl md:text-6xl mb-6">Order not found</h1>
                    <p className="text-cafe-mocha mb-8">
                        The order link you followed doesn't match anything in our system. If you just bought the kit, check your email — the correct link was sent to your inbox. If you think something's wrong, WhatsApp Kaily.
                    </p>
                    <Link to="/" className="inline-flex items-center gap-2 text-cafe-olive hover:text-cafe-oliveDark">
                        <ArrowRight className="h-4 w-4 rotate-180" /> Back to slowcafe.my
                    </Link>
                </main>
                <SiteFooter />
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-cafe-cream text-cafe-espresso" data-testid="intake-submitted">
                <SiteNav />
                <main className="pt-32 md:pt-40 pb-24 max-w-2xl mx-auto px-6">
                    <div className="text-center">
                        <CheckCircle2 className="h-12 w-12 text-cafe-olive mx-auto mb-6" strokeWidth={1.4} />
                        <div className="text-xs uppercase tracking-[0.25em] text-cafe-olive font-semibold mb-4">Brief submitted</div>
                        <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] mb-6">
                            Your kit is now <em className="font-display italic text-cafe-olive">brewing</em>.
                        </h1>
                        <p className="text-cafe-mocha max-w-lg mx-auto mb-10 leading-relaxed">
                            48 小时倒计时开始。请留意你的邮箱 —— 我们将为你打造一套属于你商店的 30 天 Instagram 文案与视觉内容，并通过邮件送达。<br className="hidden sm:block" />
                            <span className="block mt-2 text-sm italic">48-hour clock starts now. Check your inbox — we're crafting your shop's personalised 30-day Instagram content kit and delivering it by email.</span>
                        </p>
                        <div className="inline-flex items-center gap-2 text-cafe-mocha text-sm border border-border rounded-full px-5 py-2.5">
                            <Clock3 className="h-4 w-4" /> Expected delivery: within 48 hours · check email inbox
                        </div>
                        <div className="mt-12 text-xs text-cafe-mocha">
                            Need to update something? Email <a href="mailto:kailygan.my@gmail.com" className="underline">kailygan.my@gmail.com</a>
                        </div>
                    </div>
                </main>
                <SiteFooter />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-cafe-cream text-cafe-espresso" data-testid="intake-page">
            <SiteNav />
            <main className="pt-28 md:pt-36 pb-24">
                <div className="max-w-2xl mx-auto px-6 md:px-12">
                    <div className="text-xs uppercase tracking-[0.25em] text-cafe-olive font-semibold mb-4">
                        Step 2 of 2 · 5 minutes
                    </div>
                    <h1 className="font-serif text-4xl md:text-6xl text-cafe-espresso leading-[1.05] mb-4">
                        Tell me about <em className="font-display italic text-cafe-olive">your café</em>.
                    </h1>
                    <p className="text-cafe-mocha mb-2 leading-relaxed">
                        The more specific you are, the more your kit sounds like you. {isStarter ? "9" : "12"} short questions — I'll deliver within 48h of submission.
                    </p>
                    {isStarter && (
                        <div className="mb-10 bg-cafe-olive/10 border border-cafe-olive/30 rounded-2xl p-4 text-sm text-cafe-espresso leading-relaxed" data-testid="starter-intake-note">
                            <p className="font-semibold mb-1 text-cafe-olive uppercase tracking-[0.15em] text-[11px]">Starter kit · what you get</p>
                            <p className="text-cafe-mocha">
                                Brand Voice DNA · 30-day Content Calendar · 5 Caption + 20 Hook + 8 Reel-script templates · <strong>Saturday Shoot Lists</strong> (we tell you exactly what to shoot) · Visual Guide.
                            </p>
                        </div>
                    )}
                    {!isStarter && <div className="mb-10" />}

                    <form onSubmit={submit} className="flex flex-col gap-7" data-testid="intake-form">
                        <div>
                            <Label htmlFor="cafe_name" className="text-cafe-espresso font-medium">1. Café name <span className="text-cafe-terracotta">*</span></Label>
                            <Input
                                id="cafe_name"
                                data-testid="f-cafe-name"
                                value={form.cafe_name}
                                onChange={(e) => setForm({ ...form, cafe_name: e.target.value })}
                                placeholder="e.g. Slow Mornings"
                                className="mt-2 bg-cafe-cream border-border h-11"
                            />
                        </div>

                        <div>
                            <Label htmlFor="ig_handle" className="text-cafe-espresso font-medium">2. Instagram handle <span className="text-cafe-terracotta">*</span></Label>
                            <Input
                                id="ig_handle"
                                data-testid="f-ig-handle"
                                value={form.ig_handle}
                                onChange={(e) => setForm({ ...form, ig_handle: e.target.value.replace(/^@/, "") })}
                                placeholder="slowmornings.my"
                                className="mt-2 bg-cafe-cream border-border h-11"
                            />
                            <p className="text-xs text-cafe-mocha mt-1.5 leading-relaxed">
                                Why we need this: I'll personally study your existing feed — your tone, color palette, what your audience reacts to — and feed that into the AI brief. The result: 30 posts that sound like <em>you</em>, not generic café content. Your handle also gets woven naturally into captions (e.g. "Tag @yourcafe ☕️") so reposts find their way home.
                            </p>
                        </div>

                        <div>
                            <Label htmlFor="location" className="text-cafe-espresso font-medium">3. Where are you? <span className="text-cafe-terracotta">*</span></Label>
                            <Input
                                id="location"
                                data-testid="f-location"
                                value={form.location}
                                onChange={(e) => setForm({ ...form, location: e.target.value })}
                                placeholder="Subang Jaya, USJ 9"
                                className="mt-2 bg-cafe-cream border-border h-11"
                            />
                        </div>

                        <div>
                            <Label className="text-cafe-espresso font-medium">4. What kind of F&B business are you?</Label>
                            <p className="text-xs text-cafe-mocha mt-1 mb-3">Pick all that apply — multi-select. Specialty coffee, restaurant, juice bar, dessert shop, anything food or drink.</p>
                            <div className="flex flex-wrap gap-2">
                                {CAFE_TYPES.map(t => (
                                    <button
                                        type="button"
                                        key={t}
                                        data-testid={`f-type-${t}`}
                                        onClick={() => toggle("cafe_type", t)}
                                        className={`px-4 py-2 rounded-full text-sm border transition-colors ${form.cafe_type.includes(t) ? "bg-cafe-espresso text-cafe-cream border-cafe-espresso" : "bg-cafe-cream border-border text-cafe-mocha hover:border-cafe-olive"}`}
                                    >
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <Label className="text-cafe-espresso font-medium">5. Who's your customer?</Label>
                            <p className="text-xs text-cafe-mocha mt-1.5 mb-3">Pick all that apply — AI will weave each profile into the right posts.</p>
                            <div className="flex flex-wrap gap-2">
                                {CUSTOMER_PROFILES.map(t => (
                                    <button
                                        type="button"
                                        key={t}
                                        data-testid={`f-cust-${t}`}
                                        onClick={() => toggle("customer_profile", t)}
                                        className={`px-4 py-2 rounded-full text-sm border transition-colors ${form.customer_profile.includes(t) ? "bg-cafe-espresso text-cafe-cream border-cafe-espresso" : "bg-cafe-cream border-border text-cafe-mocha hover:border-cafe-olive"}`}
                                    >
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="one_word" className="text-cafe-espresso font-medium">6. Your café in one word <span className="text-cafe-terracotta">*</span></Label>
                            <p className="text-xs text-cafe-mocha mt-1.5 leading-relaxed mb-3">
                                Why we need this: This single word becomes the AI's <em>emotional compass</em> — it shapes the tone of all 30 captions. <strong>"Slow"</strong> gives long, unhurried sentences. <strong>"Sharp"</strong> gives short, punchy hooks. <strong>"Home"</strong> gives warm, family-feel copy. Pick the one that feels most YOU — or type your own.
                            </p>
                            <div className="flex flex-wrap gap-2 mb-3">
                                {ONE_WORD_OPTIONS.map((w) => {
                                    const on = form.one_word.toLowerCase() === w.id;
                                    return (
                                        <button
                                            type="button"
                                            key={w.id}
                                            data-testid={`f-oneword-${w.id}`}
                                            onClick={() => setForm({ ...form, one_word: on ? "" : w.id })}
                                            className={`px-4 py-2 rounded-full text-sm border transition-colors ${on ? "bg-cafe-espresso text-cafe-cream border-cafe-espresso" : "bg-cafe-cream border-border text-cafe-mocha hover:border-cafe-olive"}`}
                                            title={w.help}
                                        >
                                            {w.label}
                                            <span className={`ml-1.5 text-xs ${on ? "text-cafe-cream/70" : "text-cafe-mocha/60"}`}>{w.help.split(" · ")[0]}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            <Input
                                id="one_word"
                                data-testid="f-one-word"
                                value={form.one_word}
                                onChange={(e) => setForm({ ...form, one_word: e.target.value })}
                                placeholder="…or type your own (one word)"
                                className="bg-cafe-cream border-border h-11"
                            />
                        </div>

                        <div>
                            <Label htmlFor="top_menu" className="text-cafe-espresso font-medium">7. Top 5 menu items <span className="text-cafe-terracotta">*</span></Label>
                            <Textarea
                                id="top_menu"
                                data-testid="f-top-menu"
                                value={form.top_menu}
                                onChange={(e) => setForm({ ...form, top_menu: e.target.value })}
                                placeholder="Flat white, Gula Melaka Latte, Nasi Lemak, Avo Toast, Pandan Cake"
                                rows={3}
                                className="mt-2 bg-cafe-cream border-border"
                            />
                        </div>

                        {/* Q8 — Goals */}
                        <div>
                            <Label className="text-cafe-espresso font-medium">8. What are these 30 days FOR? <span className="text-cafe-terracotta">*</span></Label>
                            <p className="text-xs text-cafe-mocha mt-1 mb-3">Pick 1–2. AI will bias every post around these goals.</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {GOAL_OPTIONS.map((g) => {
                                    const on = form.goals.includes(g.id);
                                    return (
                                        <button
                                            type="button"
                                            key={g.id}
                                            data-testid={`f-goal-${g.id}`}
                                            onClick={() => toggle("goals", g.id)}
                                            className={`text-left p-3 rounded-2xl border transition-colors ${on ? "bg-cafe-olive text-cafe-cream border-cafe-olive" : "bg-cafe-cream border-border hover:border-cafe-olive"}`}
                                        >
                                            <div className={`text-sm font-semibold ${on ? "text-cafe-cream" : "text-cafe-espresso"}`}>{g.emoji} {g.label}</div>
                                            <div className={`text-xs mt-1 ${on ? "text-cafe-cream/85" : "text-cafe-mocha"}`}>{g.help}</div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Q9 — Hero menu (auto-derived from top_menu) */}
                        {form.top_menu.trim() && (
                            <div>
                                <Label className="text-cafe-espresso font-medium">9. Which 1–3 items do you want to push HARDEST?</Label>
                                <p className="text-xs text-cafe-mocha mt-1 mb-3">AI will reference these in 50%+ of posts.</p>
                                <div className="flex flex-wrap gap-2">
                                    {form.top_menu.split(/[,;\n]/).map(s => s.trim()).filter(Boolean).map((item) => {
                                        const on = form.hero_menu.includes(item);
                                        return (
                                            <button
                                                type="button"
                                                key={item}
                                                data-testid={`f-hero-${item}`}
                                                onClick={() => toggle("hero_menu", item)}
                                                className={`px-4 py-2 rounded-full text-sm border transition-colors ${on ? "bg-cafe-terracotta text-cafe-cream border-cafe-terracotta" : "bg-cafe-cream border-border text-cafe-mocha hover:border-cafe-terracotta"}`}
                                            >
                                                {on ? "★ " : ""}{item}
                                            </button>
                                        );
                                    })}
                                </div>
                                {!form.hero_menu.length && <p className="text-xs text-cafe-mocha mt-2 italic">No selection = AI distributes focus across all items equally.</p>}
                            </div>
                        )}

                        {/* Q9b — Product photos (REQUIRED for each hero item).
                            Without these, the AI invents what the dish looks
                            like — wrong shape, wrong colour, generic stock-y.
                            Format requirements are strict so renders stay
                            consistent across the 30-day grid.
                            Hidden for Starter — that tier is text-only
                            (Brand Voice + Calendar + Templates + Shoot Lists)
                            so product photos serve no AI-render purpose;
                            asking for them at RM 88 is pure friction. */}
                        {form.hero_menu.length > 0 && !isStarter && (
                            <div data-testid="product-photo-uploader" className="border-2 border-cafe-terracotta/40 rounded-2xl p-4 bg-cafe-terracotta/5">
                                <Label className="text-cafe-espresso font-semibold flex items-center gap-2">
                                    9b. Upload a photo of each starred item
                                    <span className="text-cafe-terracotta text-xs font-bold uppercase tracking-[0.15em]">Required</span>
                                </Label>
                                <p className="text-xs text-cafe-mocha mt-1.5 leading-relaxed mb-3">
                                    📸 <strong>1-3 phone photos per item</strong> — natural light, plain table, no filters. That's all we need to render YOUR actual dish (not a guess).
                                </p>
                                {form.hero_menu.map((item) => {
                                    const itemRefs = (form.product_refs || []).filter(r => r.name === item);
                                    const atMax = itemRefs.length >= 3;
                                    return (
                                        <div key={item} className="mb-4 last:mb-0 pb-3 last:pb-0 border-b last:border-b-0 border-cafe-terracotta/20">
                                            <div className="flex items-center justify-between mb-2">
                                                <p className="text-sm font-semibold text-cafe-espresso">
                                                    ★ {item}
                                                </p>
                                                <span
                                                    className={`text-[11px] font-bold uppercase tracking-[0.15em] ${
                                                        itemRefs.length === 0
                                                            ? "text-cafe-terracotta"
                                                            : atMax
                                                                ? "text-cafe-mocha"
                                                                : "text-cafe-olive"
                                                    }`}
                                                >
                                                    {itemRefs.length === 0
                                                        ? "0/3 · required"
                                                        : `${itemRefs.length}/3 photo${itemRefs.length > 1 ? "s" : ""}${atMax ? " · max" : ""}`}
                                                </span>
                                            </div>
                                            <div className="flex flex-wrap items-center gap-2.5 mb-2">
                                                <input
                                                    type="file"
                                                    multiple
                                                    accept="image/png,image/jpeg,image/jpg,image/webp,image/heic,image/heif"
                                                    data-testid={`f-product-image-${item}`}
                                                    onChange={(e) => onProductImageChange(e, item)}
                                                    disabled={uploadingProduct === item || atMax}
                                                    className="block text-xs text-cafe-mocha file:mr-2 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:text-[10px] file:font-semibold file:uppercase file:tracking-[0.15em] file:bg-cafe-terracotta/10 file:text-cafe-terracotta hover:file:bg-cafe-terracotta/20 file:cursor-pointer cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                                                />
                                                {uploadingProduct === item && <span className="text-[11px] text-cafe-terracotta italic">Uploading…</span>}
                                                {atMax && (
                                                    <span className="text-[10px] text-cafe-mocha italic">Max 3 reached. Remove one below to add another.</span>
                                                )}
                                            </div>
                                            {itemRefs.length > 0 && (
                                                <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
                                                    {itemRefs.map((ref, idx) => (
                                                        <div key={idx} className="relative aspect-square rounded-md overflow-hidden border border-border bg-cafe-latte">
                                                            <img
                                                                src={ref.image_url.startsWith("http") ? ref.image_url : `${process.env.REACT_APP_BACKEND_URL}${ref.image_url}`}
                                                                alt={`${item} ${idx + 1}`}
                                                                className="w-full h-full object-cover"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => removeProductRef(item, idx)}
                                                                className="absolute top-0.5 right-0.5 bg-cafe-terracotta text-cafe-cream text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 rounded-full hover:bg-cafe-terracotta/90"
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Q10 — Start date */}
                        <div>
                            <Label htmlFor="start_date" className="text-cafe-espresso font-medium">10. When does the 30-day plan start?</Label>
                            <p className="text-xs text-cafe-mocha mt-1 mb-3">AI checks for Malaysian holidays in this 30-day window (CNY, Raya, Mother's Day, Merdeka, Deepavali, Christmas…) and weaves themed posts on those exact days.</p>
                            <div className="flex flex-wrap items-center gap-3">
                                <Input
                                    id="start_date"
                                    type="date"
                                    data-testid="f-start-date"
                                    value={form.start_date}
                                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                                    className="bg-cafe-cream border-border h-11 max-w-xs"
                                />
                                <button
                                    type="button"
                                    onClick={() => setForm({ ...form, start_date: "" })}
                                    className="text-xs text-cafe-olive underline"
                                >
                                    Use today
                                </button>
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="tone_refs" className="text-cafe-espresso font-medium">
                                11. 3 IG accounts whose tone you love
                                <span className="ml-2 text-[11px] uppercase tracking-[0.15em] font-bold text-cafe-mocha">(optional)</span>
                            </Label>
                            <p className="text-xs text-cafe-mocha mt-1.5 leading-relaxed mb-2">
                                Why we need this: I personally open these accounts, study their rhythm — sentence length, emoji style, the kind of pauses they leave — then guide the AI to mirror that <em>feel</em>. Think of it as "write like THIS but for my café." Skip if nothing comes to mind — your kit still ships fine.
                            </p>
                            <Input
                                id="tone_refs"
                                data-testid="f-tone-refs"
                                value={form.tone_refs}
                                onChange={(e) => setForm({ ...form, tone_refs: e.target.value })}
                                placeholder="@kinfolk @hidden.bistro @your.favourite.cafe"
                                className="bg-cafe-cream border-border h-11"
                            />
                        </div>

                        <div>
                            <Label htmlFor="avoid" className="text-cafe-espresso font-medium">12. Anything to avoid?</Label>
                            <p className="text-xs text-cafe-mocha mt-1.5 leading-relaxed mb-2">
                                Why we need this: This is your <strong>safety net</strong>. Anything you write here, the AI will <em>never</em> produce in your 30 posts. Common ones: <em>"no price wars with chains" · "no English-only captions" · "no discount-heavy language" · "don't mention competitors"</em>. Leave blank if you trust the defaults.
                            </p>
                            <Textarea
                                id="avoid"
                                data-testid="f-avoid"
                                value={form.avoid}
                                onChange={(e) => setForm({ ...form, avoid: e.target.value })}
                                placeholder="No price comparisons with chains. No English-only posts."
                                rows={2}
                                className="bg-cafe-cream border-border"
                            />
                        </div>

                        {/* Q13 — Photos of your actual shop (REQUIRED · 5
                            specific angles). Without these, AI invents a
                            generic cafe vibe; with all 5 it inherits the
                            real space. Required because shoot fidelity is
                            the #1 driver of customer satisfaction.
                            Hidden for Starter — that tier only delivers 7
                            single photos so the 5-angle venue capture is
                            overkill (and would tank conversion at RM 88). */}
                        {!isStarter && (
                        <div data-testid="venue-uploader" className="border-2 border-cafe-olive/50 rounded-2xl p-4 bg-cafe-olive/10">
                            <Label className="text-cafe-espresso font-semibold flex items-center gap-2">
                                13. Photos of your actual shop
                                <span className="text-cafe-terracotta text-xs font-bold uppercase tracking-[0.15em]">Required</span>
                            </Label>
                            <p className="text-xs text-cafe-mocha mt-1.5 leading-relaxed mb-3">
                                📸 <strong>1 phone photo per angle below</strong> — natural light, no filters. AI will render every post inside YOUR real space (not a guess).
                            </p>
                            {(() => {
                                // Five fixed-angle slots. Each renders its own
                                // file picker; uploads tag onto venue_refs with
                                // the angle stored in `label` so backend keeps
                                // them distinct on R2 (venue_1.png … venue_5.png).
                                const ANGLES = [
                                    { slot: 1, label: "Storefront / signage", hint: "outside shot of your shop entrance or signage" },
                                    { slot: 2, label: "Dining area", hint: "where customers sit — wide shot showing tables / seats" },
                                    { slot: 3, label: "Bar / counter", hint: "your cashier or coffee bar — barista's view" },
                                    { slot: 4, label: "Kitchen / prep area", hint: "where food is made — even if it's small, just one angle" },
                                    { slot: 5, label: "Detail / favourite corner", hint: "a feature you love — wall art, plants, window light" },
                                ];
                                return ANGLES.map((a) => {
                                    const uploaded = (form.venue_refs || []).find(v => v.slot === a.slot);
                                    return (
                                        <div key={a.slot} className="mb-3 last:mb-0 pb-3 last:pb-0 border-b last:border-b-0 border-cafe-olive/20">
                                            <div className="flex items-center justify-between mb-2 gap-3">
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-semibold text-cafe-espresso">{a.slot}. {a.label}</p>
                                                    <p className="text-[11px] text-cafe-mocha leading-snug italic">{a.hint}</p>
                                                </div>
                                                <span className={`shrink-0 text-[11px] font-bold uppercase tracking-[0.15em] ${uploaded ? "text-cafe-olive" : "text-cafe-terracotta"}`}>
                                                    {uploaded ? "✓ uploaded" : "required"}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2.5 flex-wrap">
                                                <input
                                                    type="file"
                                                    accept="image/png,image/jpeg,image/jpg,image/webp,image/heic,image/heif"
                                                    data-testid={`f-venue-slot-${a.slot}`}
                                                    onChange={(e) => onVenueImageChange(e, a.slot, a.label)}
                                                    disabled={uploadingVenue === a.slot}
                                                    className="block text-xs text-cafe-mocha file:mr-2 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:text-[10px] file:font-semibold file:uppercase file:tracking-[0.15em] file:bg-cafe-olive/15 file:text-cafe-olive hover:file:bg-cafe-olive/25 file:cursor-pointer cursor-pointer"
                                                />
                                                {uploadingVenue === a.slot && <span className="text-[11px] text-cafe-olive italic">Uploading…</span>}
                                                {uploaded && (
                                                    <div className="flex items-center gap-2">
                                                        <img
                                                            src={uploaded.image_url.startsWith("http") ? uploaded.image_url : `${process.env.REACT_APP_BACKEND_URL}${uploaded.image_url}`}
                                                            alt={a.label}
                                                            className="w-10 h-10 rounded-md object-cover border border-border"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => removeVenueRef(a.slot)}
                                                            className="text-[10px] uppercase tracking-[0.15em] text-cafe-terracotta hover:underline"
                                                            data-testid={`venue-remove-slot-${a.slot}`}
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                        )}

                        <Button
                            type="submit"
                            disabled={submitting}
                            data-testid="submit-intake-btn"
                            className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-12 px-8 mt-2 self-start"
                        >
                            {submitting ? "Submitting…" : "Submit brief · start 48h clock"}
                            <ArrowRight className="ml-1.5 h-4 w-4" />
                        </Button>

                        <p className="text-xs text-cafe-mocha">
                            Order: {order?.id?.slice(0, 8)} · {order?.customer_email}
                        </p>
                    </form>
                </div>
            </main>
            <SiteFooter />
        </div>
    );
}
